import './Login.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';

import { useAppSelector } from '../../app/hook';
import HttpService from '../../app/http';
import { getToken } from '../../app/root';
import { useLoginMutation } from '../../app/services/auth-rtk.service';
import { persistor } from '../../app/store';
import Alert, { IAlertType } from '../../components/common/Alert';
import AuthLinkTo from '../../components/common/AuthLinkTo';
import BsInputPassword from '../../components/common/BsInputPassword';
import LanguageChangeDropdownShort from '../../components/common/LanguageChangeDropdownShort';
import SocialAuth from '../../components/common/SocialAuth';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { login as authSliceLogin, selectError, selectIsRememberMe, selectUserAuthStatus } from '../../features/auth/auth.slice';
import useAppLocalStorage from '../../hooks/useAppLocalStorage';
import useAuth from '../../hooks/useAuth';
import { SumUserTypeEnum } from '../../utils/SumUserTypeEnum';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';
import Timer from './Timer';
import { withSocialAuth } from './withSocialAuth';

type LoginType = {
  user_email: string;
  user_password: string;
  user_remember_me: boolean;
};

const BusinessLogin = (props: any) => {
  const { t } = useTranslation();
  const schema = Yup.object({
    user_email: Yup.string()
      .required(t('authBusinessPages/register/er-01'))
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        t(AlertsType.ISATKO003)
      )
      .label('Email')
      .email(),
    user_password: Yup.string().required(t('authBusinessPages/register/er-02')).label('Password'),
    user_remember_me: Yup.boolean(),
  }).required();
  const { i18n } = useTranslation();

  const dispatch = useDispatch();
  const isRememberMe = useAppSelector(selectIsRememberMe);
  const isAuthentication = useAppSelector(selectUserAuthStatus);
  const [isLoading, setIsLoading] = useState(false);
  const loginError = useAppSelector(selectError);
  const [showTimerFail, setShowTimerFail] = useState(false);
  const [ttlFail, setTtlFail] = useState(60);
  const [countFail, setCountFail] = useState(0);

  //Login Request
  const [loginUser, LoginUserMutation] = useLoginMutation();

  const localStorage = useAppLocalStorage();
  const { user, login, customer_identification } = useAuth();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location: any = useLocation();
  const [searchParams] = useSearchParams();

  const from = location.state?.from?.pathname || `/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<LoginType>({
    defaultValues: {
      user_remember_me: true,
    },
    resolver: yupResolver(schema) as any,
  });

  const [showTimer, setShowTimer] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [userId, setUserId] = useState<string>();
  const [isEmailVerificationError, setIsEmailVerificationError] = useState(false);
  const [toast, setToast] = useState<IAlertType>({
    isShow: false,
    message: '',
    type: 'warning',
  });

  const ResendEmailVerification = useMutation<any, AxiosError<any, any>, string>(
    user_identification => HttpService.post(`/auth/${user_identification}/validation-link`),
    {
      onSuccess: () => {
        // toast.success(t(AlertsType.ISATKO016));
        setShowTimer(true);
      },
      onError: err => {
        // toast.error(err.response?.data.message || err.message);
        setShowTimer(false);
      },
    }
  );

  useEffect(() => {
    if (loginError) {
      setToast({
        isShow: true,
        message: loginError ? (loginError === 'Credential error.' ? t(AlertsType.ISATKO002) : loginError) : loginError,
        type: 'Error',
      });
    }
  }, [loginError]);

  const handleResendValidationEmail = () => {
    if (userId) ResendEmailVerification.mutate(userId);
  };

  //get Profile Detail

  const closeToast = () => {
    setToast({
      isShow: false,
      message: '',
      type: 'warning',
    });
  };

  useEffect(() => {
    const persist = localStorage?.getItem(`persist:auth`);

    if (isRememberMe && isAuthentication && getToken() && persist) {
      navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
        replace: true,
      });
    }
  }, [isRememberMe, isAuthentication]);

  useEffect(() => {
    if (countFail > 5) {
      localStorage.setItem(
        `LOUISEBOT_Login`,
        JSON.stringify({
          date: Date.now().toString(),
        })
      );
      setShowTimerFail(true);
    }
  }, [countFail]);

  useEffect(() => {
    if (showTimerFail == false) {
      setCountFail(0);
    }
  }, [showTimerFail]);

  useEffect(() => {
    persistor.persist();

    const data = localStorage.getItem(`LOUISEBOT_Login`);
    if (data) {
      const { date } = JSON.parse(data);

      if (date) {
        const firstDate = new Date(+date);
        const LastDate = new Date(Date.now());
        const diffTime = Math.abs(+LastDate - +firstDate);
        const secondTime = Math.ceil(diffTime / 1000);

        if (secondTime < 60) {
          setShowTimerFail(true);
          setTtlFail(60 - secondTime);
        } else {
          setTtlFail(60);
          setShowTimerFail(false);
          localStorage.removeItem(`LOUISEBOT_Login`);
        }
      } else {
        setShowTimerFail(false);
        localStorage.removeItem(`LOUISEBOT_Login`);
      }
    }
  }, []);

  const onSubmit = (data: any) => {
    localStorage.setItem(`LOUISEBOT_token`, '');
    localStorage.setItem(`LOUISEBOT_user_access_token`, '');
    closeToast();
    setShowAlert(false);
    setIsEmailVerificationError(false);
    setIsLoading(true);
    const { user_remember_me, ...rest } = data;
    loginUser({
      user_email: data.user_email,
      user_password: data.user_password,
    })
      .unwrap()
      .then(({ token, refresh_token, userAccessToken, ...rest }) => {
        localStorage.setItem(`LOUISEBOT_token`, token);
        localStorage.setItem(`LOUISEBOT_refresh_token`, refresh_token);

        localStorage.setItem(`LOUISEBOT_user_access_token`, userAccessToken);

        dispatch(
          authSliceLogin({
            is_remember_me: user_remember_me,
          })
        );

        setIsLoading(false);

        if (rest?.plan == SumUserTypeEnum.BUSINESSPARTNER) {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/business-partner-panel`, {
            replace: true,
          });

          return;
        } else {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
            replace: true,
          });
        }
      })
      .catch(res => {
        const defaultMsg = t(AlertsType.ISGLOK001);
        const isCredentialError = res?.data?.code === 'RSUSKO001';
        const message = res?.data?.message ? (isCredentialError ? t(AlertsType.ISATKO002) : res.data.message) : res?.message || res?.data || defaultMsg;

        setToast({ isShow: true, message, type: 'Error' });
        setCountFail(c => c + 1);
        setIsLoading(false);

        if (res?.status && res.status !== 500) {
          setIsEmailVerificationError(true);
          setShowAlert(true);
          res?.data?.element?.userIdentification && setUserId(res.data.element.userIdentification);
        }
      });
  };

  return (
    <div>
      <div className="tw-bg-gray-100  tw-min-h-screen tw-h-full tw-flex tw-flex-col tw-justify-start tw-items-center md:tw-p-4  ">
        <div className="tw-text-center md:tw-h-16 tw-h-full tw-bg-gray-100 tw-my-2">
          <Link to="https://louisebot.com">
            <div className=" tw-justify-start tw-items-center tw-gap-1 tw-inline-flex">
              <img
                src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg"
                className="tw-w-10 tw-h-10 tw-relative"
                alt="LouiseBot Logo"
              />
              <div className="tw-text-blue-700 tw-text-lg tw-font-extrabold tw-font-['Lexend'] tw-leading-7">LouiseBot</div>
            </div>
          </Link>
        </div>
        <div className="md:tw-px-24   md:tw-w-auto tw-w-full md:tw-pt-[126px] tw-p-7 tw-bg-white tw-rounded-2xl tw-flex-col tw-justify-between tw-items-center tw-inline-flex">
          <div className=" tw-justify-center tw-items-center tw-gap-24 tw-inline-flex">
            <div className=" tw-self-stretch tw-flex-col tw-justify-center tw-items-start tw-gap-12 md:tw-inline-flex  tw-hidden">
              <div className="tw-self-stretch tw-h-52 tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
                <div className="tw-self-stretch tw-text-gray-800 tw-text-4xl tw-font-bold tw-font-['Lexend'] tw-leading-10">
                  {t('authBusinessPages/login/title')}
                </div>
                <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-normal tw-font-['Lexend'] tw-leading-9">
                  • {t('authBusinessPages/login/subscription/benefits/benefit_one')}
                  <br />• {t('authBusinessPages/login/subscription/benefits/benefit_two')}
                  <br />• {t('authBusinessPages/login/subscription/benefits/benefit_three')}
                  <br />• {t('authBusinessPages/login/subscription/benefits/benefit_four')}
                </div>
              </div>
            </div>
            <div className=" tw-flex-col md:tw-w-[448px] tw-w-full tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
              <div className="tw-w-full">
                <div className="">
                  <div className="md:tw-hidden tw-flex mb-4  justify-content-center align-items-center text-center tw-flex-col ">
                    <h1 className=" register-title text-center font-sofiaSans w-100 tw-text-[30px]">{t('authBusinessPages/login/title')}</h1>
                  </div>

                  {props.isSocialAuthLoading ? (
                    <div className="w-100 d-flex justify-content-center">
                      <Spinner variant="primary" animation="border" />
                    </div>
                  ) : (
                    <SocialAuth
                      className="socialAuth"
                      providers={['Google']}
                      onLoginStartSocialAuthStart={props.onLoginStartSocialAuthStart}
                      onResolveSocialAuth={props.onResolveSocialAuth}
                      onRejectSocialAuth={props.onRejectSocialAuth}
                      text={{
                        Google: t('authBusinessPages/socialAuth/loginGoogle') as string,
                      }}
                    />
                  )}

                  <div className="hor-line tw-mt-8 tw-mb-5">
                    <p className="font-sofiaSans tw-text-xs tw-font-extrabold tw-text-zinc-500 tw-px-1 md:tw-w-auto tw-w-[80%] lg:tw-w-[65%] tw-text-center">
                      {t('authBusinessPages/socialAuth/l-01')}
                    </p>
                  </div>

                  {searchParams.has('payment-status') && searchParams.get('payment-status') === 'failure' && (
                    <Alert type="Error" className="tw-mt-5">
                      {t(AlertsType.ISSBKO001)}
                    </Alert>
                  )}

                  {searchParams.has('status') && searchParams.get('status') === 'true' && (
                    <Alert type="success" className="tw-mt-5">
                      {t(AlertsType.ISSBKO002)}
                    </Alert>
                  )}

                  {isEmailVerificationError && showAlert && userId && (
                    <Alert type="warning" onClick={() => setShowAlert(false)}>
                      <div className="d-flex flex-wrap align-items-center">
                        {t(AlertsType.ISATKO001)}
                        {showTimer ? (
                          <span className="text-muted ms-2">
                            <Timer setShowTimer={setShowTimer} ttl={120} /> <>{t('authBusinessPages/register/l-07')}</>
                          </span>
                        ) : (
                          <PSButton
                            id="btn-business-resend"
                            variant="link"
                            className="p-0 border-0 ms-2"
                            style={{ verticalAlign: 'top', outline: 'none' }}
                            onClick={handleResendValidationEmail}
                            disabled={showTimer}
                            isLoading={ResendEmailVerification.isLoading}
                            loadingAnimation="grow"
                            title="Resend email"
                          />
                        )}
                      </div>
                    </Alert>
                  )}

                  {toast.isShow && (
                    <>
                      <Alert className="tw-mt-5" type={toast.type} onClick={() => closeToast()}>
                        {toast.message}
                      </Alert>
                    </>
                  )}

                  <Form onSubmit={handleSubmit(onSubmit)} className="login-form">
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="user_email" className="login-label">
                        {t('authBusinessPages/login/l-01')}
                      </Form.Label>
                      <Form.Control className="login-input" id="user_email" type="email" {...register('user_email')} />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.user_email?.message as string}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <BsInputPassword
                      label={t('authBusinessPages/login/l-02')}
                      isInvalid={!!errors.user_password}
                      errorMessage={errors.user_password?.message as string}
                      className={'mb-3'}
                      rhfControls={register('user_password')}
                    />

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="remember-me"
                        label={<span className="forgot-password">{t('authBusinessPages/login/l-03')}</span>}
                        {...register('user_remember_me')}
                      />
                      {errors?.user_remember_me?.message && <span className="error-message">{errors.user_remember_me?.message as string}</span>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <AuthLinkTo to="forgot-password" className="forgot-password">
                        {t('authBusinessPages/login/l-04')}
                      </AuthLinkTo>
                    </Form.Group>

                    <PSButton
                      id="btn-business-submit"
                      variant="primary"
                      type="submit"
                      className="w-100 d-flex justify-content-center"
                      size="lg"
                      disabled={showTimerFail || (LoginUserMutation.isLoading && isLoading) || props.isSocialAuthLoading}
                      isLoading={LoginUserMutation.isLoading && isLoading}
                      loadingAnimation="grow"
                    >
                      {showTimerFail ? <Timer setShowTimer={setShowTimerFail} ttl={ttlFail} /> : <span>{t('authBusinessPages/login/btn')}</span>}
                    </PSButton>

                    <div className="login-create-link fs-5 color-forthColor d-flex gap-2">
                      {t('authBusinessPages/login/l-06')}{' '}
                      <Link
                        to={`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}/partner/business/register`}
                        className="color-twelveColor fw-bold text-decoration-underline tw-whitespace-nowrap"
                      >
                        {t('authBusinessPages/login/l-05')}
                      </Link>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
          <div className="md:tw-mt-[88px] tw-mt-[20px] tw-text-zinc-500 tw-text-[8px] tw-font-thin tw-font-['Roboto'] tw-leading-tight">
            © LouiseBot {new Date().getFullYear()} - {import.meta.env.VITE_IMAGE_VERSION}
          </div>
        </div>

        <div className="tw-bg-gray-100 tw-w-full tw-flex tw-justify-center tw-items-center tw-p-2">
          <LanguageChangeDropdownShort theme={'bg-eightColor'} />
        </div>
      </div>

      {showPlansModal && (
        <Modal show={showPlansModal} backdrop="static" keyboard={false} size="xl">
          <Modal.Header>
            <Modal.Title>{t('authBusinessPages/login/l-07')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{/* <PlansModalBody user_identification={userId as string} /> */}</Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default WrappedErrorBoundary(withSocialAuth(BusinessLogin));
